// API服务配置
// 从环境变量获取API基础URL，默认使用http://localhost:3000/api
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000/api';

// Token管理
const TOKEN_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USERNAME: 'username'
};

// 保存token到localStorage
export const saveTokens = (accessToken, refreshToken, username) => {
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
  localStorage.setItem(TOKEN_KEYS.USERNAME, username);
};

// 获取token
export const getTokens = () => {
  return {
    accessToken: localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN),
    refreshToken: localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN),
    username: localStorage.getItem(TOKEN_KEYS.USERNAME)
  };
};

// 清除token
export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.USERNAME);
};

// 检查是否已登录
export const isLoggedIn = () => {
  return !!localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
};

// API请求封装
export async function apiRequest(endpoint, method = 'GET', data = null) {
  const url = `${API_BASE_URL}${endpoint}`;
  const tokens = getTokens();
  
  console.log(`发送API请求: ${method} ${url}`, { data, tokens });
  
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json'
    }
  };
  
  // 如果有accessToken，添加到请求头
  if (tokens.accessToken) {
    options.headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }
  
  if (data && (method === 'POST' || method === 'PUT' || method === 'DELETE')) {
    options.body = JSON.stringify(data);
  }
  
  try {
    const response = await fetch(url, options);
    
    console.log(`API响应状态: ${response.status} ${url}`);
    
    // 确保响应是JSON格式
    const contentType = response.headers.get('content-type');
    let responseData;
    
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
      console.log(`API响应数据:`, responseData);
    } else {
      const textData = await response.text();
      console.log(`API响应文本:`, textData);
      throw new Error(`响应格式错误，期望JSON: ${textData}`);
    }
    
    // 如果是401错误且不是认证相关请求（登录、注册、刷新token），才尝试刷新token
    if (response.status === 401 && 
        endpoint !== '/auth/refresh' && 
        endpoint !== '/auth/login' && 
        endpoint !== '/auth/register') {
      console.log('收到401错误，尝试刷新token');
      const refreshResponse = await refreshAccessToken();
      
      if (refreshResponse.success) {
        // 刷新成功，重新发送原请求
        console.log('token刷新成功，重新发送请求');
        const newTokens = getTokens();
        options.headers['Authorization'] = `Bearer ${newTokens.accessToken}`;
        
        const retryResponse = await fetch(url, options);
        
        if (!retryResponse.ok) {
          const retryContent = await retryResponse.json().catch(() => ({}));
          const errorMessage = retryContent.message || `请求失败: ${retryResponse.status}`;
          throw {
            success: false,
            message: errorMessage,
            error: retryContent.error || {},
            status: retryResponse.status
          };
        }
        
        const retryData = await retryResponse.json();
        console.log(`重试请求响应:`, retryData);
        return retryData;
      } else {
        // 刷新失败，清除token并跳转登录页
        console.log('token刷新失败，清除token并跳转登录页');
        clearTokens();
        window.location.href = '/';
        throw new Error('登录已过期，请重新登录');
      }
    }
    
    if (!response.ok) {
      // 直接抛出后端返回的错误数据结构
      throw {
        success: false,
        message: responseData.message || `请求失败: ${response.status}`,
        error: responseData.error || {},
        status: response.status
      };
    }
    
    return responseData;
  } catch (error) {
    console.error('API请求错误:', error);
    // 抛出包含详细信息的错误对象，确保结构一致
    throw {
      success: false,
      message: error.message || error.success === false ? error.message : 'API请求失败',
      error: error.error || error,
      stack: error.stack,
      endpoint,
      method,
      status: error.status
    };
  }
}

// 刷新AccessToken
async function refreshAccessToken() {
  const tokens = getTokens();
  
  if (!tokens.refreshToken) {
    return { success: false, message: '缺少刷新token' };
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refreshToken: tokens.refreshToken })
    });
    
    const data = await response.json();
    
    if (response.ok && data.success) {
      // 保存新的accessToken
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, data.accessToken);
      return { success: true };
    } else {
      return { success: false, message: data.message || '刷新token失败' };
    }
  } catch (error) {
    console.error('刷新token错误:', error);
    return { success: false, message: error.message };
  }
}

// 认证相关API
export const authApi = {
  // 用户登录
  login: async (username, password) => {
    console.log('发送登录请求:', { username, password });
    const response = await apiRequest('/auth/login', 'POST', { username, password });
    console.log('登录响应:', response);
    
    // 保存token
    if (response.success) {
      console.log('保存token:', { accessToken: response.accessToken, refreshToken: response.refreshToken });
      saveTokens(response.accessToken, response.refreshToken, username);
    }
    
    return response;
  },
  
  // 用户注册
  register: async (username, password) => {
    console.log('发送注册请求:', { username, password });
    const response = await apiRequest('/auth/register', 'POST', { username, password });
    console.log('注册响应:', response);
    
    // 保存token
    if (response.success) {
      console.log('保存token:', { accessToken: response.accessToken, refreshToken: response.refreshToken });
      saveTokens(response.accessToken, response.refreshToken, username);
    }
    
    return response;
  },
  
  // 检查登录状态
  check: async (username) => {
    return apiRequest(`/auth/check?username=${encodeURIComponent(username)}`);
  },
  
  // 登出
  logout: () => {
    clearTokens();
  }
};

// 材料清单相关API
export const materialListApi = {
  // 获取所有材料清单（使用token认证）
  getAll: async () => {
    return apiRequest('/materialList/all');
  },
  
  // 获取单个材料清单
  getOne: async (id) => {
    return apiRequest(`/materialList/${id}`);
  },
  
  // 创建材料清单
  create: async (data) => {
    return apiRequest('/materialList/add', 'POST', data);
  },
  
  // 更新材料清单
  update: async (id, data) => {
    // 将id添加到data对象中，以便后端从请求体中获取
    const updateData = { ...data, id };
    return apiRequest('/materialList/update', 'PUT', updateData);
  },
  
  // 删除材料清单
  delete: async (id) => {
    return apiRequest(`/materialList/delete/${id}`, 'DELETE');
  },
  
  // 导出材料清单
  export: async () => {
    return apiRequest('/materialList/export');
  }
};
